/**
 * ScrollFade — reusable bottom-gradient "scroll continuation" wrapper.
 *
 * When a scroll container's content is taller than its box, a hard visual
 * fold at the bottom reads as "broken" (a card cut in half) rather than
 * "there's more below." ScrollFade owns the scroll container itself
 * (`overflow-auto`, prop-driven sizing via `scrollClassName`) and overlays a
 * `pointer-events-none` bottom gradient, theme-aware via the `--popover`
 * token, that is visible only while more content sits below the fold.
 *
 * Detection: a 1px sentinel div is appended after `children` inside the
 * scroll body. An `IntersectionObserver` (rooted at the scroll container)
 * reports whether the sentinel is in view — intersecting ⇒ bottom reached
 * ⇒ fade hidden; not intersecting ⇒ more content below ⇒ fade shown. This
 * avoids scroll-event thrash. Environments without `IntersectionObserver`
 * (none expected at runtime — Electron/Chromium always has it) fall back to
 * a scroll-driven geometry check.
 *
 * Generic and compact-popover-agnostic: no assumptions about caller markup
 * beyond "children live in a scrollable box." Full-mode cards can reuse it
 * unchanged.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '../../../../../lib/utils';

export interface ScrollFadeProps {
  readonly children: ReactNode;
  /** `data-testid` on the fade overlay element. */
  readonly testId?: string;
  /** Extra classes on the outer (`relative`) wrapper. */
  readonly className?: string;
  /** Extra classes on the inner scroll container (the `overflow-auto` box). */
  readonly scrollClassName?: string;
  /** Extra classes on the fade overlay itself. */
  readonly fadeClassName?: string;
}

const DEFAULT_TEST_ID = 'vqe-scroll-fade';

export function ScrollFade({
  children,
  testId = DEFAULT_TEST_ID,
  className,
  scrollClassName,
  fadeClassName,
}: ScrollFadeProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Hidden until the detection mechanism proves there's more content below —
  // avoids a flash of fade on content that never scrolls.
  const [showFade, setShowFade] = useState(false);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      const update = () => {
        const canScrollFurther = root.scrollHeight - root.scrollTop - root.clientHeight > 1;
        setShowFade(canScrollFurther);
      };
      update();
      root.addEventListener('scroll', update, { passive: true });
      return () => root.removeEventListener('scroll', update);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setShowFade(!entry.isIntersecting);
      },
      { root, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    // The wrapper is a flex column and the scroll box takes `min-h-0 flex-1`
    // rather than `h-full`: a percentage height only resolves when every
    // ancestor's height is definite, and the compact popover's chain is capped
    // by `max-h` alone (indefinite) — `h-full` there collapsed to content
    // height, so the "scroll" box never scrolled and its content spilled over
    // the popover's pinned footer. Flex sizing needs no percentage resolution.
    <div className={cn('relative flex min-h-0 flex-col', className)}>
      <div ref={scrollRef} className={cn('min-h-0 flex-1 overflow-auto', scrollClassName)}>
        {children}
        <div ref={sentinelRef} aria-hidden="true" className="h-px w-full shrink-0" />
      </div>
      <div
        aria-hidden="true"
        data-testid={testId}
        data-visible={showFade}
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 h-8',
          'bg-gradient-to-t from-popover to-transparent',
          'transition-opacity duration-150',
          showFade ? 'opacity-100' : 'opacity-0',
          fadeClassName,
        )}
      />
    </div>
  );
}
